// janus-engine/movement/walk-trap.ts — Janus session 3 (Movement) step 4, 2026-09-29: a WALK's trap fire, decided in the browser.
//
// Twin of ft_trap_check_step_triggers() (includes/functions/janus/core/ft_trap_fire.php), which move_encounter_creature_logic.php runs
// BEFORE each 1-ft foot of a walk: for every inventory host carrying the Trapped status whose ARMED row (a Trapped row with no end time)
// stands and that mounts a trap payload, the first mounted payload (in mount order) whose trap_trigger this foot satisfies fires at the
// mover —
//   magical_creature_detection  the planar distance from the foot to the host ≤ the payload's aoe_distance (else 5 ft);
//   step_on / touch             the mover's body AT the foot overlaps the host's body (ft_ground_occupancy_shape, centred spans —
//                               ft_move_occupancy_overlap: ft3d-move-core.js occupancyOverlap, the golden twin);
// — and the walk halts: the step is not taken, the mover stays on the previous foot (a fired foot is never charged). Every host is judged
// at a foot, so two can fire on the same one.
// Answers `clear`, `fire` (the foot, the host, the payload, the verb) or `handback` (a payload
// definition not held, a body the page cannot shape, two hosts firing at once, the very first foot).
// Oracle: tools/probes/movement_trap_walk_oracle.php → tools/test/fixtures/movement-trap-walk-oracle.json (tools/test/janus-movement-walk-trap.test.mjs).
import { occupancyOverlap } from '../../../../ft3d/engine/ft3d-move-core.js';
import type { Zone } from '../inventory/floor-move.js';
import { ftTrapArmedStatusRow } from '../port/k4-dispatch/ft_trap_fire.js';
import { browserDependencies } from '../port/browser-dependencies.js';
import type { ReadEnv } from '../port/k4-dispatch/shared.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();

export interface WalkTrapReaders {
  /** creature_statuses(row, 'target') — the page's stored rows (a participant's cache keeps the private armed row). */
  statuses: (row: Row) => Row[];
  /** The Trapped status item id (StatusEffect 'trapped'). */
  trappedId: string;
  /** ft_trap_mounted_ability_ids(): the host's trap_ability layer + its template's trap_ability slots (trap-truth.ts). */
  mountedIds: (host: Row) => string[];
  /** A payload definition (query_full_item_arr); null = not held → hand back. */
  definition: (id: string) => Row | null;
  /** layer_entries / layer_value on a definition. */
  entries: (def: Row, key: string) => unknown[];
  value: (def: Row, key: string) => unknown;
  /** The host's position (x, y) and its body (ft_ground_occupancy_shape($host), centred span); null = not provable. */
  hostXY: (host: Row) => [number, number];
  hostZone: (host: Row) => Zone | null;
  /** The mover's body at a foot (ft_ground_occupancy_shape($mover, x, y), centred span); null = not provable. */
  moverZoneAt: (x: number, y: number) => Zone | null;
}
export type WalkTrapVerdict = { kind: 'clear' }
  | { kind: 'fire'; step: number; foot: [number, number]; hostId: string; abilityId: string; verb: 'magical_creature_detection' | 'step_on' | 'touch' }
  | { kind: 'handback'; reason: string };

const statusItem = (s: Row): string => upper(record(s.items)?.item_id ?? s.status_item_id ?? record(record(s.status_arr)?.items)?.item_id);

/** `feet` = legacyStepPositions(route): index 0 is the start (never stepped). */
export function resolveWalkTrap(inventories: readonly Row[], feet: ReadonlyArray<readonly [number, number]>, read: WalkTrapReaders): WalkTrapVerdict {
  const trapped = upper(read.trappedId);
  type Host = { row: Row; id: string; mounted: Array<{ id: string; triggers: string[]; radius: number }>; xy: [number, number]; zone: Zone | null | undefined };
  const hosts: Host[] = [];
  for (const row of inventories) {
    const id = upper(record(row.inventory)?.inventory_id);
    if (!id || Number(scalar(record(row.inventory)?.inventory_deleted) ?? 0) === 1) continue;
    const rows = read.statuses(row).filter(s => statusItem(s) === trapped && s.status_id);
    if (!rows.length) continue;
    const mountedIds = read.mountedIds(row);
    if (!mountedIds.length) continue;
    const armed = ftTrapArmedStatusRow(row, browserDependencies({
      creature_statuses: () => rows,
      item_field: (status: unknown) => statusItem(status as Row),
    }, { 'StatusEffect::$idCache': { trapped } }) as unknown as ReadEnv);
    if (!armed) continue;
    const mounted: Host['mounted'] = [];
    for (const mid of mountedIds) {
      const def = read.definition(mid);
      if (!def) return { kind: 'handback', reason: 'walk-trap:definition-not-held' };
      const triggers = read.entries(def, 'trap_trigger').map(t => String(typeof t === 'string' ? t : record(t)?.item_id ?? '').trim().toLowerCase());
      const raw = Number(scalar(read.value(def, 'aoe_distance')));
      mounted.push({ id: upper(mid), triggers, radius: Number.isFinite(raw) && raw > 0 ? raw : 5 });
    }
    hosts.push({ row, id, mounted, xy: read.hostXY(row), zone: undefined });
  }
  if (!hosts.length) return { kind: 'clear' };
  for (let i = 1; i < feet.length; i++) {
    const [sx, sy] = feet[i]!;
    const fires: Array<Extract<WalkTrapVerdict, { kind: 'fire' }>> = [];
    let moverZone: Zone | null | undefined;
    for (const host of hosts) {
      let onHost: boolean | null = null; // lazy, per host per foot (the PHP closure)
      const stepOnHost = (): boolean | null => {
        if (onHost !== null) return onHost;
        if (host.zone === undefined) host.zone = read.hostZone(host.row);
        if (moverZone === undefined) moverZone = read.moverZoneAt(sx, sy);
        if (!host.zone || !moverZone) return null;
        onHost = occupancyOverlap(moverZone as never, host.zone as never) as boolean;
        return onHost;
      };
      for (const payload of host.mounted) {
        let verb: 'magical_creature_detection' | 'step_on' | 'touch' | '' = '';
        if (payload.triggers.includes('magical_creature_detection') && Math.hypot(sx - host.xy[0], sy - host.xy[1]) <= payload.radius) verb = 'magical_creature_detection';
        if (verb === '' && (payload.triggers.includes('step_on') || payload.triggers.includes('touch'))) {
          const on = stepOnHost();
          if (on === null) return { kind: 'handback', reason: 'walk-trap:body' };
          if (on) verb = payload.triggers.includes('step_on') ? 'step_on' : 'touch';
        }
        if (verb === '') continue;
        fires.push({ kind: 'fire', step: i, foot: [sx, sy], hostId: host.id, abilityId: payload.id, verb });
        break; // one armed charge per host per step
      }
    }
    if (fires.length > 1) return { kind: 'handback', reason: 'walk-trap:multiple-hosts' };
    if (fires.length === 1) return i === 1 ? { kind: 'handback', reason: 'walk-trap:first-foot' } : fires[0]!;
  }
  return { kind: 'clear' };
}
